import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    Button,
    Group,
    Select,
    Stack,
    Text,
} from '@mantine/core';

import {
    CertificatesFilters,
    useCertificatesFilter,
} from '../hooks/useCertificatesQuery';

// FNP-1193 — the filter form.
//
// ⚠️ EMPTY VALUES ARE NEVER STORED, so `Object.keys(filters).length` is an
// honest count of how many filters are actually narrowing the table. The badge
// on the filter button reads that count.
export const FilterForm = (
    {
        onApply,
        onCancel,
    }: {
        onApply: () => void,
        onCancel: () => void,
    },
) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useCertificatesFilter();

    const [status, setStatus] = useState<string | null>(filters.status ?? null);
    const [targetType, setTargetType] = useState<string | null>(filters.targetType ?? null);

    // ⚠️ `NEEDS_ATTENTION` IS FIRST AND IS NOT A STATUS.
    //
    // It matches STUCK *and* VALIDATION_NOT_WRITTEN — the two states that do
    // not resolve by waiting. They stay separately selectable below, because
    // they are different problems with different fixes, but "show me what is
    // not progressing" is one question and making an operator ask it twice is
    // how the two get treated as interchangeable again.
    const statusOptions = [
        {value: 'NEEDS_ATTENTION', label: I18n.get('Needs attention (stuck or not written)')},
        {value: 'ISSUED', label: I18n.get('Issued')},
        {value: 'PENDING_VALIDATION', label: I18n.get('Pending validation')},
        {value: 'STUCK', label: I18n.get('Validation stuck')},
        {value: 'VALIDATION_NOT_WRITTEN', label: I18n.get('Validation record not written')},
        {value: 'FAILED', label: I18n.get('Failed')},
        {value: 'EXPIRED', label: I18n.get('Expired')},
        {value: 'REVOKED', label: I18n.get('Revoked')},
    ];

    const targetOptions = [
        {value: 'CLOUDFRONT', label: I18n.get('CloudFront')},
        {value: 'LOAD_BALANCER', label: I18n.get('Load balancer')},
        {value: 'API_GATEWAY', label: I18n.get('API Gateway')},
        {value: 'UNSPECIFIED', label: I18n.get('Not specified')},
    ];

    const handleApply = () => {
        const next: CertificatesFilters = {};
        if (status) {
            next.status = status;
        }
        if (targetType) {
            next.targetType = targetType;
        }
        handleSetFilters(next);
        onApply();
    };

    const handleClear = () => {
        setStatus(null);
        setTargetType(null);
        handleClearFilters();
        onApply();
    };

    return (
        <Stack gap={'md'}>

            <Text size={'xs'} c={'dimmed'}>
                {/* ⚠️ SAYS THE FILTER IS OVER LOADED ROWS. `listCertificates`
                    takes no filter argument at all, so this narrows what the
                    browser has fetched rather than querying the server. An
                    operator who filters and sees fewer rows than they expect
                    needs to know to load more first. */}
                {I18n.get('Filters apply to the certificates loaded so far. Load more to widen what they search.')}
            </Text>

            <Select
                clearable={true}
                label={I18n.get('Status')}
                placeholder={I18n.get('Any status')}
                data={statusOptions}
                value={status}
                onChange={setStatus}
            />

            <Select
                clearable={true}
                label={I18n.get('Target')}
                placeholder={I18n.get('Any target')}
                data={targetOptions}
                value={targetType}
                onChange={setTargetType}
            />

            <Group justify={'flex-end'}>
                <Button variant={'subtle'} onClick={handleClear}>
                    {I18n.get('Clear')}
                </Button>
                <Button variant={'default'} onClick={onCancel}>
                    {I18n.get('Cancel')}
                </Button>
                <Button onClick={handleApply}>
                    {I18n.get('Apply')}
                </Button>
            </Group>

        </Stack>
    );
};
